/**
 * AddOpportunityModal - Presentation Layer
 * 
 * Modal for adding a new opportunity via:
 * 1. Pasting a listing URL (Zillow, Redfin, etc.)
 * 2. Dropping a pin on map (coordinates passed in)
 * 3. Manually entering an address
 * 
 * Uses useCreateOpportunity hook for business logic.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TextInput,
  Pressable,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, icon, space } from '../../../shared/tokens/mobile';
import { useCreateOpportunity, OpportunityInput } from '../../../hooks/useCreateOpportunity';
import { Opportunity } from '../../../shared/types/opportunity';
import { styles } from './addOpportunityStyles';

// =============================================================================
// Types
// =============================================================================

export type AddMode = 'url' | 'address' | 'coordinates';

interface AddOpportunityModalProps {
  visible: boolean;
  portfolioId: number;
  onClose: () => void;
  onSuccess: (opportunity: Opportunity) => void;
  initialCoordinates?: { latitude: number; longitude: number };
  initialMode?: AddMode;
}

// =============================================================================
// Sub-Components
// =============================================================================

function ModeTab({ label, iconName, active, onPress }: {
  label: string;
  iconName: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable style={[styles.modeTab, active && styles.modeTabActive]} onPress={onPress}>
      <Ionicons name={iconName as any} size={icon.md} color={active ? color.primary : color.textSecondary} />
      <Text style={[styles.modeTabText, active && styles.modeTabTextActive]}>{label}</Text>
    </Pressable>
  );
}

// =============================================================================
// Component
// =============================================================================

export function AddOpportunityModal({
  visible,
  portfolioId,
  onClose,
  onSuccess,
  initialCoordinates,
  initialMode = 'url',
}: AddOpportunityModalProps) {
  const { create, loading, error, clearError } = useCreateOpportunity();
  
  // Form state
  const [mode, setMode] = useState<AddMode>(initialMode);
  const [url, setUrl] = useState('');
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [askingPrice, setAskingPrice] = useState('');
  const [acreage, setAcreage] = useState('');
  const [notes, setNotes] = useState('');
  const [showOptional, setShowOptional] = useState(false);

  // Reset form on open
  useEffect(() => {
    if (visible) {
      setMode(initialCoordinates ? 'coordinates' : initialMode);
      setUrl('');
      setAddress('');
      setName('');
      setAskingPrice('');
      setAcreage('');
      setNotes('');
      setShowOptional(false);
      clearError();
    }
  }, [visible, initialCoordinates, initialMode, clearError]);

  const handleSubmit = useCallback(async () => {
    const input: OpportunityInput = {
      name: name.trim() || undefined,
      askingPrice: askingPrice ? parseFloat(askingPrice.replace(/[^0-9.]/g, '')) : undefined,
      acreage: acreage ? parseFloat(acreage) : undefined,
      notes: notes.trim() || undefined,
    };

    if (mode === 'url') input.listingUrl = url.trim();
    else if (mode === 'address') input.address = address.trim();
    else if (mode === 'coordinates' && initialCoordinates) input.coordinates = initialCoordinates;

    const opportunity = await create(portfolioId, input);
    if (opportunity) {
      onSuccess(opportunity);
      onClose();
    }
  }, [mode, url, address, name, askingPrice, acreage, notes, initialCoordinates, portfolioId, create, onSuccess, onClose]);

  const canSubmit = mode === 'url' ? url.trim().length > 0 :
                    mode === 'address' ? address.trim().length > 0 :
                    mode === 'coordinates' ? !!initialCoordinates : false;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Add Opportunity</Text>
          <View style={styles.closeButton} />
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          {/* MODE SELECTOR */}
          {!initialCoordinates && (
            <View style={styles.modeSelector}>
              <ModeTab label="Paste URL" iconName="link-outline" active={mode === 'url'} onPress={() => setMode('url')} />
              <ModeTab label="Address" iconName="location-outline" active={mode === 'address'} onPress={() => setMode('address')} />
            </View>
          )}

          {/* COORDINATES INDICATOR */}
          {mode === 'coordinates' && initialCoordinates && (
            <View style={styles.coordsCard}>
              <Ionicons name="pin" size={icon.lg} color={color.primary} />
              <View style={styles.coordsContent}>
                <Text style={styles.coordsTitle}>Pin Dropped</Text>
                <Text style={styles.coordsText}>
                  {initialCoordinates.latitude.toFixed(5)}, {initialCoordinates.longitude.toFixed(5)}
                </Text>
              </View>
            </View>
          )}

          {/* URL INPUT */}
          {mode === 'url' && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Listing URL</Text>
              <TextInput
                style={styles.input}
                value={url}
                onChangeText={setUrl}
                placeholder="Paste Zillow, Redfin, or other listing URL"
                placeholderTextColor={color.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="url"
                autoFocus
              />
              <Text style={styles.hint}>Supports Zillow, Redfin, Realtor.com, LandWatch, and more</Text>
            </View>
          )}

          {/* ADDRESS INPUT */}
          {mode === 'address' && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Address</Text>
              <TextInput
                style={styles.input}
                value={address}
                onChangeText={setAddress}
                placeholder="123 Main St, City, State ZIP"
                placeholderTextColor={color.textMuted}
                autoFocus
              />
            </View>
          )}

          {/* OPTIONAL FIELDS TOGGLE */}
          <Pressable style={styles.optionalToggle} onPress={() => setShowOptional(!showOptional)}>
            <Text style={styles.optionalToggleText}>{showOptional ? 'Hide optional details' : 'Add optional details'}</Text>
            <Ionicons name={showOptional ? 'chevron-up' : 'chevron-down'} size={icon.sm} color={color.textSecondary} />
          </Pressable>

          {/* OPTIONAL FIELDS */}
          {showOptional && (
            <View style={styles.optionalFields}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Name (optional)</Text>
                <TextInput style={styles.input} value={name} onChangeText={setName} placeholder="Give it a nickname" placeholderTextColor={color.textMuted} />
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, { flex: 1 }]}>
                  <Text style={styles.label}>Asking Price</Text>
                  <TextInput style={styles.input} value={askingPrice} onChangeText={setAskingPrice} placeholder="$0" placeholderTextColor={color.textMuted} keyboardType="numeric" />
                </View>
                <View style={[styles.inputGroup, { flex: 1, marginLeft: space.md }]}>
                  <Text style={styles.label}>Acreage</Text>
                  <TextInput style={styles.input} value={acreage} onChangeText={setAcreage} placeholder="0" placeholderTextColor={color.textMuted} keyboardType="decimal-pad" />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Notes</Text>
                <TextInput style={[styles.input, styles.textArea]} value={notes} onChangeText={setNotes} placeholder="Any initial thoughts..." placeholderTextColor={color.textMuted} multiline numberOfLines={3} />
              </View>
            </View>
          )}

          {/* ERROR */}
          {error && (
            <View style={styles.errorCard}>
              <Ionicons name="alert-circle" size={icon.md} color={color.error} />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}
        </ScrollView>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Pressable style={[styles.submitButton, (!canSubmit || loading) && styles.submitButtonDisabled]} onPress={handleSubmit} disabled={!canSubmit || loading}>
            {loading ? (
              <ActivityIndicator color={color.surface} />
            ) : (
              <>
                <Ionicons name="add-circle" size={icon.md} color={color.surface} />
                <Text style={styles.submitButtonText}>Add Opportunity</Text>
              </>
            )}
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
